import { Component, AfterViewInit, ChangeDetectorRef, ElementRef, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { LoginModalComponent } from '../login-modal/login-modal.component';
import { CommonModule } from '@angular/common';
import { AuthService } from '../../../services/auth.service';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { DepositModalComponent } from '../deposit-modal/deposit-modal.component';
import { ModalHelperComponent } from '../modal-helper/modal-helper.component';
import { ClipboardModule } from '@angular/cdk/clipboard';
import { ToastService } from '../../../services/toast.service';
import { Web3ServiceService } from '../../../services/web3-service.service';
import { ApiService } from '../../../services/api.service';
import { EnableModalComponent } from '../enable-modal/enable-modal.component';
import { PageScrollLock } from '../../../common/utils/dropdown-viewport';

/** Narrowest viewport that gets the profile dropdown instead of the full-screen menu.
 *  Matches the stylesheets' (width < 576px) boundary exactly, so no fractional width
 *  can be mobile in CSS and desktop here. */
const DESKTOP_MENU_MIN_WIDTH = 576;

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [
    CommonModule,
    LoginModalComponent,
    RouterModule,
    DepositModalComponent,
    ModalHelperComponent,
    ClipboardModule,
    EnableModalComponent
  ],
  templateUrl: './header.component.html',
  styleUrl: './header.component.css'
})
export class HeaderComponent implements OnInit, AfterViewInit, OnDestroy {
  modalOpen = false;
  dropdownOpen = false;
  private dropdownCooldown = false;
  depositModalOpen = false;
  isTransferCryptoOpen = false;
  userData: any;
  usdcBalance: any = 0;
  portfolioValue: any = 0;
  enableTradingModalOpen = false;
  enableProfileFlowModalOpen = false;
  /** Drives the full-screen profile menu that replaces the dropdown at 575px and below */
  mobileMenuOpen = false;
  /** Where the menu starts: the bottom of the sticky header, in px */
  mobileMenuTop = 0;
  private portfolioGlobalSubscription: Subscription | undefined;
  private activeStreamUserId: string | null = null;
  private headerResizeObserver?: ResizeObserver;

  get isVerifyEmailPage(): boolean {
    const isVerify = this.router.url.includes('/verify-email') || this.router.url.includes('/callback');
    // console.log(isVerify, 'isVerifyEmailPage');
    return isVerify;
  }

  constructor(
    private router: Router,
    public auth: AuthService,
    private toast: ToastService,
    private api: ApiService,
    private web3Service: Web3ServiceService,
    private cdr: ChangeDetectorRef,
    private host: ElementRef<HTMLElement>
  ) { }

  ngOnInit() {
    this.userData = this.auth.getUserData()
    this.auth.operation
      .subscribe((name: string) => {
        if (name == "login") {
          this.openModal();
          this.cdr.detectChanges();
        } else if (name == "enable-trading") {
          this.openEnableFlow();
          this.cdr.detectChanges();
        }
      });

    this.auth.isConnected$
      .subscribe((result: boolean) => {
        if (result === true) {
          this.userData = this.auth.getUserData();
          this.dropdownOpen = false;
          this.dropdownCooldown = true;
          setTimeout(() => this.dropdownCooldown = false, 500);

          // Only fetch profile if AppComponent hasn't already done it (e.g. after a fresh login)
          if (this.userData?.id && !this.auth.isInitialProfileLoaded) {
            this.getProfileData(this.userData.id);
          }
        } else if (result === false) {
          this.userData = {};
          this.usdcBalance = 0;
          this.portfolioValue = 0;
          this.dropdownOpen = false;
          this.cdr.detectChanges();
        }
      });

    // Subscribe to the global stream managed by AuthService
    this.portfolioGlobalSubscription = this.auth.portfolioSummary$.subscribe({
      next: (res: any) => {
        if (!res) return;
        // console.log("Header UI Update from Global Stream:", res);
        // Map the fields from the streaming response
        this.usdcBalance = res.cash_balance ?? res.data?.cash_balance ?? this.usdcBalance;
        this.portfolioValue = res.portfolio_value ?? res.data?.portfolio_value ?? this.portfolioValue;
        this.cdr.detectChanges();
      }
    });
    // Close dropdown on any route change
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.dropdownOpen = false;
        this.closeMobileMenu();
      }
    });

  }

  ngAfterViewInit(): void {
    // The header is sticky at the top of every page and the nav bar sticks
    // directly below it, so the nav bar needs the header's real height rather
    // than a hard-coded guess: the logo, the breakpoint and the logged-in
    // controls all change how tall the header ends up being.
    this.publishHeaderHeight();
    this.headerResizeObserver = new ResizeObserver(() => this.publishHeaderHeight());
    this.headerResizeObserver.observe(this.host.nativeElement);
  }

  ngOnDestroy() {
    if (this.portfolioGlobalSubscription) {
      this.portfolioGlobalSubscription.unsubscribe();
    }
    // The lock is on <body>, so it has to be released even if the menu was open
    this.lockBodyScroll(false);
    this.headerResizeObserver?.disconnect();
  }

  /**
   * Publishes the header's height as --header-height, the offset everything
   * that sticks underneath it is positioned from.
   * @returns {void}
   */
  private publishHeaderHeight(): void {
    // Rounded up: half a pixel short would let the page scroll through the seam
    // between the header and whatever sticks below it.
    const height = Math.ceil(this.host.nativeElement.getBoundingClientRect().height);
    if (height > 0) {
      document.documentElement.style.setProperty('--header-height', `${height}px`);
    }
  }

  /**
   * The name shown at the top of the profile menu
   * Falls back to the wallet address when no user name has been set yet
   * Long values are shortened in the middle so the end of an address stays readable
   * @returns The label for the signed-in user
   */
  get profileLabel(): string {
    const name = this.userData?.user_name || this.userData?.profile_display_name;
    return this.shortenMiddle(name || this.userData?.proxy_wallet || '');
  }

  /**
   * Cuts the middle out of a value that is too long for the profile menu,
   * keeping both ends visible instead of losing the tail to a CSS ellipsis
   * @param value - The full text to display
   * @returns The value itself when short enough, otherwise its head and tail joined by an ellipsis
   */
  private shortenMiddle(value: string): string {
    if (value.length <= 19) return value;
    return `${value.slice(0, 8)}...${value.slice(-8)}`;
  }

  // Local stream removed - now using global stream from AuthService

  /**
   * Get user profile
   * @param userId 
   */
  getProfileData(userId: any) {
    if (!userId) return;
    this.api
      .getUserProfile(userId)
      .subscribe(async (result: any) => {
        this.userData = result.data;
        this.cdr.detectChanges();
      });
  }

  openDropdown() {
    if (this.dropdownCooldown) return;
    if (this.isMobileViewport()) return;
    this.dropdownOpen = true;
  }

  /**
   * Handles a tap on the profile picture
   * Below 576px the dropdown has nowhere to sit, so the same entry point opens
   * the full-screen menu instead of going straight to the profile page
   */
  onProfileClick(): void {
    if (this.isMobileViewport()) {
      this.openMobileMenu();
      return;
    }

    this.router.navigate(['/profile']);
  }

  /**
   * Opens the full-screen profile menu
   */
  openMobileMenu(): void {
    this.dropdownOpen = false;
    this.updateMobileMenuTop();
    this.mobileMenuOpen = true;
    this.lockBodyScroll(true);
  }

  /**
   * Closes the full-screen profile menu
   */
  closeMobileMenu(): void {
    this.mobileMenuOpen = false;
    this.lockBodyScroll(false);
  }

  /**
   * Sends the user to a menu destination and dismisses the menu
   * @param commands - Router commands for the destination
   * @param extras - Optional query parameters for the destination
   */
  goTo(commands: any[], extras?: { [key: string]: string }): void {
    this.closeMobileMenu();
    this.router.navigate(commands, extras ? { queryParams: extras } : undefined);
  }

  /**
   * Logs the user out from the mobile menu
   */
  logoutFromMenu(): void {
    this.closeMobileMenu();
    this.auth.logout();
  }

  /**
   * Whether the viewport is narrow enough to use the full-screen menu
   * @returns true below 576px, false otherwise
   */
  private isMobileViewport(): boolean {
    return !window.matchMedia(`(min-width: ${DESKTOP_MENU_MIN_WIDTH}px)`).matches;
  }

  /**
   * Measures where the sticky header ends so the menu opens directly below it
   */
  private updateMobileMenuTop(): void {
    const host = document.querySelector('app-header');
    this.mobileMenuTop = host ? Math.round(host.getBoundingClientRect().bottom) : 0;
  }

  /**
   * Stops the page behind the menu from scrolling while it is open
   * @param locked - Whether the page should be held still
   */
  private lockBodyScroll(locked: boolean): void {
    this.scrollLock.set(locked);
  }

  /**
   * Shared with the popups on the pages below, so whichever closes last is the
   * one that gives the page its scrolling back
   */
  private scrollLock = new PageScrollLock();

  /**
   * Drops the menu once the viewport is wide enough for the dropdown again, so
   * it cannot linger over the desktop layout
   */
  @HostListener('window:resize')
  onWindowResize(): void {
    if (!this.mobileMenuOpen) return;

    if (!this.isMobileViewport()) {
      this.closeMobileMenu();
    } else {
      // The header can change height as it reflows, so re-anchor the open menu
      this.updateMobileMenuTop();
    }
  }

  /**
   * Closes the menu when Escape is pressed
   */
  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    this.closeMobileMenu();
  }

  navigateToPortfolio() {
    this.router.navigate(['/portfolio']);
  }

  closeDropdown() {
    this.dropdownOpen = false;
  }

  openModal() {
    this.modalOpen = true;
  }

  depositModal() {
    if (!this.auth.isLoggedIn()) {
      this.auth.sendOperationSignal("login");
      return;
    }

    if (!this.auth.isTradeEnabled()) {
      this.enableTradingModalOpen = true;
      return;
    }
    this.depositModalOpen = true;
  }

  closeDepositModal() {
    this.depositModalOpen = false;
  }

  closeEnableTradingModal() {
    this.enableTradingModalOpen = false;
  }

  closeEnableProfileFlowModal() {
    this.enableProfileFlowModalOpen = false;
    // Show enable-trading modal only after profile flow for new users.
    this.enableTradingModalOpen = true;
  }

  private openEnableFlow(): void {
    this.dropdownOpen = false;
    const rawIsExistingUser = this.userData?.is_existing_user;

    const isExistingUser = typeof rawIsExistingUser === 'string'
      ? rawIsExistingUser.toLowerCase() === 'true'
      : rawIsExistingUser === true;

    if (isExistingUser === false) {
      this.enableProfileFlowModalOpen = true;
      this.enableTradingModalOpen = false;
      return;
    }

    this.enableTradingModalOpen = true;
    this.enableProfileFlowModalOpen = false;
  }

  copyText(): void {
    navigator
      .clipboard
      .writeText(this.userData.proxy_wallet)
      .then(() => {
        this.toast.success("Address copied successfully");
      })
  }
}
